import {
  requestInterceptors,
  BlockingRequestInterceptor,
  AxiosRequestConfig,
  ReportRequestInterceptor,
  AbortRequestInterceptor,
  axios,
} from '@tencent/wphk-base-request';
import { hideDialog, showDialog } from '@tencent/wphk-components-dialog';
import { i18n } from '../i18n';
import { HandleLoginParams } from '../types/index';
import ConsumeTokenIntercetor  from './consume-token-intercetor';

/**
 * 默认登陆超时错误码
 */
const DEF_ERROR_CODES = ['51001097', '51001000', '8598998000'];

/**
 * OAuth授权code请求失败错误码
 */
export const OAUTH_CODE_ERROR_CODES = ['8598999005', '8598999003', '8598999006', '8598999004'];

/**
 * 非香港钱包用户错误码
 */
export const IS_NOT_HK_USER_CODES = ['8598999016', '8598999017'];

/**
 * 登陆拦截处理参数
 */
interface LoginBlockOptions {
  /**
     * 登陆错误码列表，可不填
     */
  errCodes?: string[],
  /**
   * 登陆前处理，如关闭loading
    */
  beforeLogin?: (params: HandleLoginParams) => void,
  /**
   * 接口请求检测到登陆失效，尝试token预登陆，返回登陆结果
   * @returns
   */
  preLogin: (config: AxiosRequestConfig, isLoggedIn: boolean) => Promise<boolean>;
  /**
   * 页面登陆
   */
  getOAuthCode: (params: HandleLoginParams) => void,
  /**
   * 登录态检查
   * @param params
   */
  login: (config: AxiosRequestConfig) => Promise<void>,
}

/**
 *  根据上次的接口信息，重新发起请求
 * @param responseConfig axios接口请求信息
 * @returns
 */
async function replayRequest(responseConfig) {
  try {
    // 对之前请求报登陆超时的接口，再次发起请求，如果网络请求报错则忽略，走后续auth登陆流程
    return await axios(responseConfig);
  } catch (error) {
    return null;
  }
}

/**
 * 处理登陆超时，跳转获取授权码登录，拦截未登录请求
 */
export function initRequestBlockInterceptors(options: {
  /**
     * 登陆态过期处理相关参数
     */
  blockingRequest?: LoginBlockOptions
}) {
  const { blockingRequest } = options;
  if (blockingRequest) {
    const { errCodes, beforeLogin, getOAuthCode, login, preLogin } = blockingRequest;
    const blockErrCodes: string[] = [DEF_ERROR_CODES, IS_NOT_HK_USER_CODES, OAUTH_CODE_ERROR_CODES].flat();
    const errorCodes = [... new Set(blockErrCodes.concat(errCodes || []))];
    requestInterceptors
      .use(new BlockingRequestInterceptor({
        errCodes: errorCodes,
        waitRequestHandle: async (config) => {
          await login(config);
        },
        waitUpdateResponse: async (response, isLoggedIn) => {
          const { config } = response;
          const isPreLoginSuccss = await preLogin(config, isLoggedIn);
          // 预登陆成功才会去重拉数据
          if (isPreLoginSuccss) {
            return await replayRequest(config);
          }
          return null;
        },
        waitResponseHandle: (params: HandleLoginParams) => {
          const { isLoggedIn } = params;
          // 一般ajax请求前会先展示loading，由于登陆拦截会卡住后续流程，
          // 这里需要先手动关闭loading，避免loading和下面的登陆提示弹框同时出现
          if (beforeLogin) {
            beforeLogin(params);
          }
          // 操作引发的超时失效，需要弹框引导登录
          if (isLoggedIn) {
            showDialog({
              title: i18n('你的登录已失效，请重新登录。'),
              btns: [
                {
                  text: i18n('确定'),
                  click() {
                    hideDialog();
                    getOAuthCode(params);
                  },
                },
              ],
            });
            return;
          }
          // 页面初始化时登陆失效，直接去进行登陆操作
          getOAuthCode(params);
        },
      })).use(new ConsumeTokenIntercetor(errorCodes));
  }
  // 添加通用的日志上报和请求中断拦截器
  requestInterceptors
    .use(new ReportRequestInterceptor())
    .use(new AbortRequestInterceptor());
}
